import { CANCELLED_STATUSES, SUCCESS_STATUSES } from './constants'
import { IOrderReportBucket, ISaleOrderDetail } from './types'

// map settlement ต่อ order → bucket เดียว (count = 1) เพื่อ reuse SettlementBreakdownTable
// field ต้องตรงกับ settlementGroupFields ฝั่ง platform (report ระดับ aggregate)
export const settlementToBucket = (
  detail: ISaleOrderDetail,
): IOrderReportBucket => {
  const s = detail.settlement
  const b = s?.breakdown
  const isSuccess = SUCCESS_STATUSES.includes(detail.status)
  const isCancelled = CANCELLED_STATUSES.includes(detail.status)

  return {
    status: detail.status,
    count: 1,
    quantity: detail.quantity,
    codTotal: detail.is_cod ? detail.total_amount : 0,
    transferTotal: detail.is_cod ? 0 : detail.total_amount,
    grandTotal: detail.total_amount,
    successCount: isSuccess ? 1 : 0,
    successTotal: isSuccess ? detail.total_amount : 0,
    cancelledCount: isCancelled ? 1 : 0,
    cancelledTotal: isCancelled ? detail.total_amount : 0,
    // bucket นี้ใช้กับ SettlementBreakdownTable (ไม่แสดงต้นทุน) → ใส่ 0 ให้ครบ type
    successCostTotal: 0,
    cancelledCostTotal: 0,
    costTotal: 0,
    costMissingCount: 0,
    revenueTotal: s?.revenue ?? 0,
    netTotal: s?.net_amount ?? 0,
    feeTotal: s?.fee_total ?? 0,
    commissionTotal: s?.commission ?? 0,
    settledCount: detail.settled ? 1 : 0,
    grossSalesTotal: b?.gross_sales ?? 0,
    sellerDiscountTotal: b?.seller_discount ?? 0,
    salesRefundTotal: b?.sales_refund ?? 0,
    salesRefundGrossTotal: b?.sales_refund_gross ?? 0,
    salesRefundSellerTotal: b?.sales_refund_seller ?? 0,
    platformDiscountTotal: b?.platform_discount ?? 0,
    customerPaymentTotal: b?.customer_payment ?? 0,
    affiliateTotal: b?.affiliate ?? 0,
    affiliateCommissionTotal: b?.affiliate_commission ?? 0,
    affiliateAdsTotal: b?.affiliate_ads ?? 0,
    affiliatePartnerTotal: b?.affiliate_partner ?? 0,
    shippingFeeTotal: b?.shipping_fee ?? 0,
    shippingSubsidyTotal: b?.shipping_subsidy ?? 0,
    otherFeeTotal: b?.other_fee ?? 0,
    refundTotal: b?.refund ?? 0,
    adjustmentTotal: b?.adjustment ?? 0,
  }
}

// TikTok 202309 line_items = 1 entry ต่อชิ้น (ไม่มี field quantity) → group ตาม sku นับจำนวน
export interface ILineItemRow {
  key: string
  product_name: string
  sku_name: string
  sku_image: string
  seller_sku: string
  unit_price: number
  quantity: number
  subtotal: number
  currency: string
  unit_cost: number // ต้นทุน/ชิ้น (0 ถ้าหาไม่เจอ)
  cost: number // unit_cost * quantity
}

// costLineItems = line_items ที่ backend snapshot ต้นทุนไว้ (key ด้วย seller_sku = product.code)
// ราคา/ชื่อ/รูป มาจาก raw, ต้นทุน merge เข้าด้วย seller_sku
export const groupLineItems = (
  raw?: Record<string, any>,
  costLineItems?: { seller_sku: string; unit_cost: number }[],
): ILineItemRow[] => {
  const lineItems: any[] = raw?.line_items ?? []
  const costByCode = new Map<string, number>()
  ;(costLineItems ?? []).forEach((li) => {
    if (li.seller_sku) costByCode.set(li.seller_sku, li.unit_cost ?? 0)
  })

  const map = new Map<string, ILineItemRow>()

  lineItems.forEach((item) => {
    const key = `${item.product_id ?? ''}-${item.sku_id ?? ''}`
    const unitPrice = Number(item.sale_price ?? 0)
    const existing = map.get(key)

    if (existing) {
      existing.quantity += 1
      existing.subtotal += unitPrice
      existing.cost = existing.unit_cost * existing.quantity
    } else {
      const unitCost = costByCode.get(item.seller_sku ?? '') ?? 0
      map.set(key, {
        key,
        product_name: item.product_name ?? '-',
        sku_name: item.sku_name ?? '',
        sku_image: item.sku_image ?? '',
        seller_sku: item.seller_sku ?? '',
        unit_price: unitPrice,
        quantity: 1,
        subtotal: unitPrice,
        currency: item.currency ?? '',
        unit_cost: unitCost,
        cost: unitCost,
      })
    }
  })

  return Array.from(map.values())
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100

// กำไรประมาณการ (real-time) = ยอดขายสำเร็จ − ต้นทุนสินค้า − ค่าธรรมเนียม − ค่าใช้จ่ายอื่น
// feeTotal เก็บเป็นค่าลบอยู่แล้ว (ยอดที่ถูกหัก) จึงบวกเข้าไปตรง ๆ
// ค่าธรรมเนียมมีเฉพาะออเดอร์ที่ TikTok ออก statement แล้ว — ช่วงที่ยังไม่โอน กำไรจะสูงกว่าจริง
export const calcEstimatedProfit = (
  sales: Pick<
    IOrderReportBucket,
    'successTotal' | 'successCostTotal' | 'feeTotal'
  >,
  otherCost: number,
): number =>
  roundMoney(
    (sales.successTotal ?? 0) -
      (sales.successCostTotal ?? 0) +
      (sales.feeTotal ?? 0) -
      (otherCost ?? 0),
  )

// กำไรสุทธิจริง (settled) = ยอดที่ได้รับจริง − ต้นทุนสินค้า − ค่าใช้จ่ายอื่น
// netTotal หักค่าธรรมเนียมแล้ว แต่นับเฉพาะออเดอร์ที่ TikTok โอนแล้ว — วันที่ยังโอนไม่ครบจะต่ำ (ติดลบได้) จนกว่าจะโอน
export const calcSettledProfit = (
  sales: Pick<IOrderReportBucket, 'netTotal' | 'successCostTotal'>,
  otherCost: number,
): number =>
  roundMoney(
    (sales.netTotal ?? 0) - (sales.successCostTotal ?? 0) - (otherCost ?? 0),
  )
